import { useEffect, useState } from "react";
import { Alert, Button, MenuItem, Stack, TextField } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { isApiError } from "@bw/shared/api";
import type {
  CommercialOfferPublicationReview,
  CommercialPublicationRequest,
} from "@bw/shared/types/generated";
import { PlatformPage } from "@/components/PlatformPage";
import { ModuleOfferPublicationFacts } from "../components/ModuleOfferEditor";
import {
  useModuleOfferPublicationReview,
  usePublishModuleOffer,
} from "../hooks/useModuleOfferAuthoring";

function PublicationController({ draftId }: { draftId: string }) {
  // Sandbox is the initial review mode, not approval or a publish default.
  const [environment, setEnvironment] =
    useState<CommercialPublicationRequest["environment"]>("sandbox");
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [captured, setCaptured] = useState<{
    review: CommercialOfferPublicationReview;
    request: CommercialPublicationRequest;
  } | null>(null);
  const query = useModuleOfferPublicationReview(draftId, environment);
  const mutation = usePublishModuleOffer(draftId);
  const navigate = useNavigate();
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const denied =
    (query.isError && isApiError(query.error) && query.error.status === 403) ||
    (mutation.isError &&
      isApiError(mutation.error) &&
      mutation.error.status === 403);
  const terminal =
    isApiError(mutation.error) &&
    mutation.error.status >= 400 &&
    mutation.error.status < 500 &&
    mutation.error.status !== 408 &&
    mutation.error.status !== 429;
  useEffect(() => {
    if (denied) setAuthorityDenied(true);
  }, [denied]);
  if (denied || authorityDenied)
    return (
      <Alert severity="warning">
        Current Admin publication access unavailable. Review current authority
        before continuing.{" "}
        <Button
          size="small"
          onClick={async () => {
            const result = await query.refetch();
            if (
              result.isSuccess &&
              result.data.candidate.draft_id === draftId &&
              result.data.candidate.environment === environment
            ) {
              // Current read access does not resolve an unknown POST outcome.
              if (!captured || terminal) {
                setCaptured(null);
                setKey(crypto.randomUUID());
                mutation.reset();
              }
              setAuthorityDenied(false);
            }
          }}
        >
          Review current access
        </Button>
      </Alert>
    );
  const current = query.data;
  const blocked =
    !current ||
    !current.source_available ||
    current.environment_allowed !== true ||
    Boolean(current.unresolved_gates.length || current.blockers.length);
  const displayed = captured?.review ?? current;
  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        select
        label="Review environment"
        value={environment}
        disabled={mutation.isPending || Boolean(captured)}
        onChange={(e) => setEnvironment(e.target.value as typeof environment)}
      >
        <MenuItem value="sandbox">Sandbox</MenuItem>
        <MenuItem value="production">Production</MenuItem>
      </TextField>
      {query.isError ? (
        <Alert severity="warning">
          Current publication review unavailable.{" "}
          <Button size="small" onClick={() => void query.refetch()}>
            Retry review
          </Button>
        </Alert>
      ) : displayed && displayed.candidate.draft_id === draftId ? (
        <>
          {captured && blocked ? (
            <Alert severity="warning">
              Current publication source is unavailable or unresolved. The
              original attempt is retained, not replaced.
            </Alert>
          ) : null}
          <ModuleOfferPublicationFacts
            review={displayed}
            attempt={
              captured?.request ?? {
                expected_revision: displayed.candidate.draft_revision,
                environment,
                idempotency_key: key,
              }
            }
            isPending={mutation.isPending || terminal || (!captured && blocked)}
            error={
              mutation.isError
                ? terminal
                  ? "Publication was denied. Reload and review before another attempt."
                  : "Publication result unknown. Retry the original attempt without changing its body."
                : null
            }
            onPublish={(request) => {
              if (mutation.isPending || terminal || (!captured && blocked))
                return;
              setCaptured(
                (original) => original ?? { review: displayed, request },
              );
              mutation.mutate(captured?.request ?? request, {
                onSuccess: (result) =>
                  navigate(
                    `/contracts/module-offers/versions/${result.offer_version_id}`,
                  ),
              });
            }}
          />
        </>
      ) : query.isPending ? (
        <Alert severity="info" role="status">
          Loading publication candidate…
        </Alert>
      ) : null}
      {terminal ? (
        <Button
          size="small"
          variant="outlined"
          onClick={async () => {
            const result = await query.refetch();
            if (result.isSuccess) {
              setCaptured(null);
              setKey(crypto.randomUUID());
              mutation.reset();
            }
          }}
        >
          Reload and review candidate
        </Button>
      ) : null}
    </Stack>
  );
}

export function ModuleOfferPublicationPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { user } = useAuth();
  return (
    <PlatformPage
      title="Module offer publication review"
      actions={
        <Button size="small" component={Link} to="/contracts/module-offers">
          Back to module drafts
        </Button>
      }
    >
      {draftId && user?.uid ? (
        <PublicationController
          key={`${user.uid}:${draftId}`}
          draftId={draftId}
        />
      ) : (
        <Alert severity="warning">
          Current Admin draft context unavailable.
        </Alert>
      )}
    </PlatformPage>
  );
}
